import { useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, RotateCcw, UserPlus } from "lucide-react";
import { ContactPicker, useLenderContacts } from "@/components/LenderOutreach";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const field = "bg-slate-950/60 border-slate-700";
const FIELDS = [["officer_name", "Loan officer name"], ["officer_title", "Officer title"], ["lender_name", "Bank or lender"], ["officer_email", "Officer email"], ["subject", "Subject line"]];

const SaveContact = ({ f, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/lender-contacts", { name: f.officer_name, title: f.officer_title || "", bank: f.lender_name || "", email: f.officer_email || null, address: f.lender_address || "" });
      toast.success(`${f.officer_name} saved to lender contacts`); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Button type="button" size="sm" variant="ghost" disabled={busy || !f.officer_name?.trim()} onClick={save} className="text-slate-300" data-testid="cover-letter-save-contact">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}Save officer to contacts</Button>;
};

export const CoverLetterButton = ({ studyId, enabled, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  const [contacts, reloadContacts] = useLenderContacts();
  const load = async (fresh = false) => {
    setBusy(fresh ? "reset" : "load");
    try {
      const r = await api.get(`/studies/${studyId}/cover-letter`, { params: fresh ? { fresh: true } : {} });
      const keep = (d, cur) => Object.fromEntries(Object.entries(d).map(([k, v]) => [k, ["body", "subject"].includes(k) || !cur[k] ? v : cur[k]]));
      setF((cur) => (fresh && cur ? { ...keep(r.data, cur), enabled: cur.enabled } : r.data));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const show = () => { setOpen(true); load(); };
  const save = async () => {
    setBusy("save");
    try { const r = await api.put(`/studies/${studyId}/cover-letter`, f); onSaved?.(r.data); toast.success(r.data.enabled ? "Cover letter will be included in PDF and Word" : "Cover letter saved, not included"); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <>
      <Button variant="outline" onClick={show} data-testid="cover-letter-button" className="rounded-full border-slate-700 bg-transparent">
        <Mail className="h-4 w-4 mr-2" />Cover letter{enabled && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-400">On</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cover-letter-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Lender cover letter</DialogTitle>
            <DialogDescription className="text-slate-400">A formal letter to the client's loan officer, placed in front of the study in PDF and Word downloads.</DialogDescription>
          </DialogHeader>
          {!f ? <Loader2 className="h-5 w-5 animate-spin text-emerald-400" /> : (
            <div className="space-y-4">
              <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 p-3">
                <span className="text-sm">Include in this study's downloads</span>
                <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="cover-letter-enabled" />
              </label>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2"><Label className="text-xs uppercase tracking-wider text-slate-400">Addressed to</Label><SaveContact f={f} onSaved={reloadContacts} /></div>
                <ContactPicker contacts={contacts} testId="cover-letter-picker" onPick={(c) => c && setF({ ...f, officer_name: c.name, officer_title: c.title || "", lender_name: c.bank || "", lender_address: c.address || "", officer_email: c.email || "" })} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {FIELDS.map(([k, l]) => (
                  <div key={k} className={`space-y-1.5 ${k === "subject" ? "sm:col-span-2" : ""}`}>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">{l}</Label>
                    <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={field} data-testid={`cover-letter-${k}`} />
                  </div>
                ))}
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Lender address</Label>
                  <Textarea rows={2} value={f.lender_address || ""} onChange={(e) => setF({ ...f, lender_address: e.target.value })} className={field} data-testid="cover-letter-lender_address" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Letter</Label>
                  <Textarea rows={12} value={f.body || ""} onChange={(e) => setF({ ...f, body: e.target.value })} className={`${field} leading-relaxed`} data-testid="cover-letter-body" />
                  <p className="text-xs text-slate-500">Leave a blank line between paragraphs. The date, salutation and your signature are added automatically.</p>
                </div>
              </div>
              <div className="flex flex-wrap justify-between gap-2">
                <Button variant="ghost" disabled={!!busy} onClick={() => load(true)} data-testid="cover-letter-reset">{busy === "reset" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}Refill from study</Button>
                <Button disabled={!!busy} onClick={save} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400" data-testid="cover-letter-save">{busy === "save" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save letter</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
